import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  lazy,
  Suspense
} from 'react';
import { debounce } from 'lodash';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Popover,
  Button,
  Divider,
  FormControlLabel,
  Slider,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { GridToolbar } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import '../App.css';
import logger from '../logger.js';
// Bucket map for EV Age tint
const ageToBucket = (ageSec) => {
  if (ageSec < 60) return 0;
  if (ageSec < 120) return 1;
  if (ageSec < 180) return 2;
  if (ageSec < 240) return 3;
  if (ageSec < 300) return 4;
  return 5;
};

const bucketColors = [
  'rgba(0,255,0,0.75)',
  'rgba(0,255,0,0.6)',
  'rgba(0,255,0,0.45)',
  'rgba(0,255,0,0.3)',
  'rgba(0,255,0,0.15)',
  'transparent',
];

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  components: {
    MuiInputLabel: { 
      styleOverrides: { 
        root: { 
          color: '#ffffff',
          fontSize: '0.875rem',
        } 
      } 
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
        },
      },
    },
    MuiMenu: { 
      styleOverrides: { 
        paper: { 
          backgroundColor: '#333333', 
          color: '#ffffff' 
        } 
      } 
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { 
        root: { 
          color: '#ffffff', 
          '&.Mui-checked': { color: '#ff1744' } 
        } 
      } 
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#ff1744',
        },
        thumb: {
          backgroundColor: '#ff1744',
          '&:hover': {
            boxShadow: '0 0 0 8px rgba(255, 23, 68, 0.16)',
          },
        },
        track: {
          backgroundColor: '#ffffff',
        },
        rail: {
          backgroundColor: '#555555',
        },
        valueLabel: {
          backgroundColor: '#333333',
          color: '#ffffff',
          fontSize: '0.75rem',
        },
        markLabel: {
          color: '#ffffff',
          fontSize: '0.75rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#333333',
          color: '#ffffff',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
  },
});

// Helper function for EV color
const getEVColor = (value) => {
  if (value == null) return '#ffffff';
  const val = value * 100;
  let r, g, b;
  if (val <= -10) {
    r = 255; g = 0; b = 0;
  } else if (val > -10 && val <= 0) {
    const t = (val + 10) / 10;
    r = 255; g = Math.round(255 * t); b = Math.round(255 * t);
  } else if (val > 0 && val <= 10) {
    const t = val / 10;
    r = Math.round(255 * (1 - t)); g = 255; b = Math.round(255 * (1 - t));
  } else {
    r = 0; g = 255; b = 0;
  }
  return `rgb(${r},${g},${b})`;
};

// Memoized renderCell components
const RoiCell = React.memo(({ value }) => {
  if (value == null) return '-';
  const val = value * 100; // Convert to percentage
  let r, g, b;

  // Color logic
  if (val <= -10) {
    r = 255;
    g = 0;
    b = 0;
  } else if (val > -10 && val <= 0) {
    const t = (val + 10) / 10;
    r = 255;
    g = Math.round(255 * t);
    b = Math.round(255 * t);
  } else if (val > 0 && val <= 10) {
    const t = val / 10;
    r = Math.round(255 * (1 - t));
    g = 255;
    b = Math.round(255 * (1 - t));
  } else {
    r = 0;
    g = 255;
    b = 0;
  }

  // Format with fixed width: 2 digits before decimal, 1 after, plus '%'
  const formatted = val.toFixed(1).padStart(4, ' ') + '%'; // e.g., "10.5%", " 8.1%"
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ color: `rgb(${r},${g},${b})`, fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {formatted}
      </Typography>
    </Box>
  );
});

const GrowthRateCell = React.memo(({ value }) => {
  if (value == null) return '-';
  const val = value * 100; // Convert to percentage
  let r, g, b;

  if (val <= 0) {
    r = 255;
    g = 255;
    b = 255;
  } else if (val > 0 && val <= 0.5) {
    const t = val / 0.5;
    r = Math.round(204 * (1 - t));
    g = 255;
    b = Math.round(204 * (1 - t));
  } else {
    r = 0;
    g = 255;
    b = 0;
  }

  return <span style={{ color: `rgb(${r},${g},${b})` }}>{val.toFixed(2)}%</span>;
});

const KellyCell = React.memo(({ value, bankroll, kellyFraction }) => {
  const baseKellyValue = (value ?? 0) * bankroll;
  const adjustedKellyValue = baseKellyValue * (kellyFraction ?? 1);
  return adjustedKellyValue == null ? '' : `$${Math.round(adjustedKellyValue)}`;
});

const RoiAgeCell = React.memo(({ ageSeconds, displayValue, snipeMaxAge }) => {
  if (!displayValue) return null;
  const bucket = ageToBucket(ageSeconds, snipeMaxAge);
  return (
    <Box sx={{
      backgroundColor: bucketColors[bucket],
      color: '#ffffff',
      borderRadius: 0,
      width: 'calc(100% + 16px)', // Extend beyond cell padding
      height: 'calc(100% + 16px)', // Extend beyond cell padding
      margin: '-8px', // Negative margin to counteract cell padding
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: '0.875rem',
      transition: 'background-color 0.5s ease',
      pl: '16px', // Adjusted padding for text
    }}>
      <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre' }}>
        {displayValue}
      </Typography>
    </Box>
  );
});

const DeeplinkCell = React.memo(({ value, isAuthenticated }) => {
  const disabled = !value || !isAuthenticated;
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'left', justifyContent: 'flex-start' }}>
      <Button
        onClick={() => { if (!disabled) window.open(value, '_blank', 'width=1000,height=800,scrollbars=yes'); }}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ?                       '#666666' : '#00ff00',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          '&.Mui-disabled': { color: 'gray' },
        }}
      >
        Bet
      </Button>
    </Box>
  );
});

const TrackBetCell = React.memo(({ row, trackedBets, toggleTrack, trackBet, untrackBet, trackingInProgress, immediatelyTracked, isAuthenticated }) => {
  const id = row.id;
  const isTracked = trackedBets.some(bet => bet.id === id) || immediatelyTracked.has(id);
  const isDisabled = !trackBet || !untrackBet || trackingInProgress.has(id) || !isAuthenticated;

  // Debug logging for duplicate detection
  useEffect(() => {
    const matchingBets = trackedBets.filter(bet => bet.id === id);
    if (matchingBets.length > 1) {
      logger.error(`[UI] Multiple instances of bet ${id} in trackedBets!`, matchingBets);
    }
  }, [trackedBets, id]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          logger.log(`[UI] Button clicked for bet ${id}, isTracked: ${isTracked}, isDisabled: ${isDisabled}`);
          if (!isDisabled) {
            toggleTrack(row, trackBet, untrackBet);
          }
        }}
        size="small"
        sx={{ padding: 0, color: isTracked ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isTracked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </IconButton>
    </Box>
  );
});

const HideBetCell = React.memo(({ row, hiddenBets, setHiddenBets, toggleHide, showHiddenBets, isAuthenticated }) => {
  const id = row.id;
  const isHidden = hiddenBets.includes(id);
  const isDisabled = !isAuthenticated;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleHide(row);
            const updated = isHidden
              ? hiddenBets.filter(x => x !== id)
              : [...hiddenBets, id];
            setHiddenBets(updated);
            localStorage.setItem('hiddenBets', JSON.stringify(updated));
          }
        }}
        size="small"
        sx={{ padding: 0, color: isHidden ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={(isHidden && !showHiddenBets) || isDisabled}
      >
        {isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </Box>
  );
});

// Mobile Bet Card Component
const MobileBetCard = React.memo(({ 
  bet, 
  bankroll, 
  kellyFraction, 
  trackedBets, 
  hiddenBets, 
  setHiddenBets,
  showHiddenBets,
  toggleTrack,
  toggleHide,
  trackBet,
  untrackBet,
  trackingInProgress,
  associatedBets,
  isBestOdds,
  immediatelyTracked,
  isAuthenticated,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isTracked = trackedBets.some(tb => tb.id === bet.id) || immediatelyTracked.has(bet.id);
  const isHidden = hiddenBets.includes(bet.id);
  const isTrackingDisabled = !trackBet || !untrackBet || trackingInProgress.has(bet.id) || !isAuthenticated;

  const kellyValue = bet.kelly ? Math.round(bet.kelly * bankroll * kellyFraction) : 0;

  // Format event date
  const eventDate = bet.eventStart
    ? new Date(bet.eventStart).toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).replace(',', '')
    : '';

  return (
    <Card sx={{ 
      mb: 1.5, 
      border: '1px solid #333',
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1}>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              {bet.playerName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.75rem' }}>
              {bet.betTypeName}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => !isTrackingDisabled && toggleTrack(bet, trackBet, untrackBet)}
              disabled={isTrackingDisabled}
              sx={{ 
                p: 0.5,
                color: isTracked ? '#00ff00' : '#888',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: '#444',
                }
              }}
            >
              {isTracked ? <CheckBoxIcon sx={{ fontSize: 20 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                if (isAuthenticated) {
                  const id = bet.id;
                  const isHidden = hiddenBets.includes(id);
                  
                  // Update hiddenBets state
                  const updatedHiddenBets = isHidden
                    ? hiddenBets.filter(x => x !== id)
                    : [...hiddenBets, id];
                  
                  setHiddenBets(updatedHiddenBets);
                  localStorage.setItem('hiddenBets', JSON.stringify(updatedHiddenBets));
                  
                  // Call the original toggleHide function
                  toggleHide(bet);
                }
              }}
              disabled={(isHidden && !showHiddenBets) || !isAuthenticated}
              sx={{ 
                p: 0.5,
                color: isHidden ? '#00ff00' : '#888',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: '#444',
                }
              }}
            >
              {isHidden ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Event Info */}
        <Box mb={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {/* League name */}
            {bet.leagueName && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem', mr: 0.5 }}>
                  {bet.leagueName}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.875rem', mx: 0.5 }}>
                  |
                </Typography>
              </>
            )}
            {/* Matchup with logos */}
            {(() => {
              const [awayTeam, homeTeam] = bet.eventShortName.split('@');
              return (
                <>
                  {bet.awayLogo && (
                    <img
                      src={bet.awayLogo}
                      alt={`${awayTeam} Logo`}
                      style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                      loading="lazy"
                    />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {awayTeam}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '0.75rem', mx: '2px' }}>
                    @
                  </Typography>
                  {bet.homeLogo && (
                    <img
                      src={bet.homeLogo}
                      alt={`${homeTeam} Logo`}
                      style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                      loading="lazy"
                    />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {homeTeam}
                  </Typography>
                </>
              );
            })()}
          </Box>
          <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.75rem' }}>
            {eventDate}
          </Typography>
        </Box>

        {/* Book and Line with X More button */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <img
              src={bet.logourl}
              alt={bet.marketSourceName}
              style={{ width: 48, height: 48, objectFit: 'contain' }}
            />
            <Box sx={{ 
              backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bet.sidepoints} {bet.price < 0 ? bet.price : `+${bet.price}`}
              </Typography>
            </Box>
          </Stack>
          
          {/* X More button next to odds */}
          {associatedBets && associatedBets.length > 1 && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
              sx={{ minWidth: 'auto' }}
            >
              {associatedBets.length - 1} More
            </Button>
          )}
        </Stack>

        {/* Associated Bets Accordion - Right after the X More button */}
        {expanded && associatedBets && associatedBets.length > 1 && (
          <Box mb={1.5}>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem', mb: 1, display: 'block' }}>
              Other Books:
            </Typography>
            {(() => {
              // Include the main bet in the list and sort all bets
              const allBets = associatedBets;
              
              // Parse and sort all bets by line first, then by price
              const parsedBets = allBets.map(bet => ({
                ...bet,
                parsedSide: bet.side || '',
                parsedLine: Number(bet.points) || 0,
              }));

              const isOver = parsedBets[0]?.parsedSide === 'Over';
              const orderedBets = parsedBets.sort((a, b) => {
                const lineA = a.parsedLine;
                const lineB = b.parsedLine;
                const priceA = a.price ?? -Infinity;
                const priceB = b.price ?? -Infinity;

                if (lineA !== lineB) {
                  return isOver ? lineA - lineB : lineB - lineA;
                }
                return priceB - priceA;
              });

              return orderedBets.slice(0, 10).map((assocBet, idx) => (
                <Stack 
                  key={idx} 
                  direction="row" 
                  alignItems="center" 
                  spacing={1} 
                  sx={{ 
                    mt: 1,
                    backgroundColor: assocBet.id === bet.id ? 'rgba(0, 150, 255, 0.2)' : 'transparent',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5
                  }}
                >
                  <img
                    src={assocBet.logourl}
                    alt={assocBet.marketSourceName}
                    style={{ width: 32, height: 32, objectFit: 'contain' }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {assocBet.sidepoints} {assocBet.price < 0 ? assocBet.price : `+${assocBet.price}`}
                  </Typography>
                </Stack>
              ));
            })()}
          </Box>
        )}

        {/* Key Metrics Row with Place Bet button */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Grid container spacing={2} sx={{ flex: 1 }}>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                EV
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: getEVColor(bet.roi),
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                {bet.roi ? `${(bet.roi * 100).toFixed(1)}%` : '-'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                Growth%
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: bet.growthRate > 0 ? '#00ff00' : '#ffffff',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                {bet.growthRate ? `${(bet.growthRate * 100).toFixed(2)}%` : '-'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                Kelly
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                ${kellyValue}
              </Typography>
            </Grid>
          </Grid>
          
          {/* Place Bet button on the right */}
          <Button
            size="small"
            variant="contained"
            onClick={() => bet.deeplink && isAuthenticated && window.open(bet.deeplink, '_blank')}
            disabled={!bet.deeplink || !isAuthenticated}
            sx={{
              backgroundColor: '#00ff00',
              color: '#000',
              minWidth: 'auto',
              px: 2,
              ml: 2,
              '&:hover': { backgroundColor: '#00cc00' },
              '&.Mui-disabled': { backgroundColor: '#333', color: '#666' },
            }}
          >
            Bet
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
});

const BetsDataGrid = ({ bets, trackBet, untrackBet, trackedBets, isAuthenticated }) => {
  const theme = customTheme;

  // State initialization
  const [sortModel, setSortModel] = useState([{ field: 'growthRate', sort: 'desc' }]);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [bankroll, setBankroll] = useState(() => {
    const saved = localStorage.getItem('bankroll');
    return saved ? parseFloat(saved) : 1000;
  });
  const [kellyFraction, setKellyFraction] = useState(() => {
    const saved = localStorage.getItem('kellyFraction');
    return saved ? parseFloat(saved) : 1;
  });
  const [hideTracked, setHideTracked] = useState(() => {
    const saved = localStorage.getItem('hideTracked');
    return saved ? JSON.parse(saved) : true;
  });
  const [showHiddenBets, setShowHiddenBets] = useState(() => {
    const saved = localStorage.getItem('showHiddenBets');
    return saved ? JSON.parse(saved) : false;
  });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [snipeMinEV, setSnipeMinEV] = useState(() => {
    const saved = localStorage.getItem('snipeMinEV');
    return saved !== null && saved !== 'null' ? parseFloat(saved) : null;
  });
  const [snipeMaxAge, setSnipeMaxAge] = useState(() => {
    const saved = localStorage.getItem('snipeMaxAge');
    return saved !== null && saved !== 'null' ? parseInt(saved) : null;
  });
  const [minEVDisplay, setMinEVDisplay] = useState('');
  const [maxAgeDisplay, setMaxAgeDisplay] = useState('');
  const [breakEvenWinRange, setBreakEvenWinRange] = useState(() => {
    const saved = localStorage.getItem('breakEvenWinRange');
    return saved ? JSON.parse(saved) : [0, 100];
  });
  const [leagueFilter, setLeagueFilter] = useState(() => {
    const saved = localStorage.getItem('leagueFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [categoryFilter, setCategoryFilter] = useState(() => {
    const saved = localStorage.getItem('categoryFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [betTypeFilter, setBetTypeFilter] = useState(() => {
    const saved = localStorage.getItem('betTypeFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [bookFilter, setBookFilter] = useState(() => {
    const saved = localStorage.getItem('bookFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('dateRange');
    return saved ? (saved === 'null' ? null : Number(saved)) : 7;
  });
  const [hiddenBets, setHiddenBets] = useState(() => {
    const s = localStorage.getItem('hiddenBets');
    const parsed = s ? JSON.parse(s) : [];
    return parsed.slice(-1000);
  });
  const [pendingRemoval, setPendingRemoval] = useState(new Set());
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const [immediatelyTracked, setImmediatelyTracked] = useState(new Set());
  
  const gridContainerRef = useRef(null);

  useEffect(() => {
    const gridContainer = gridContainerRef.current;
    if (!isAuthenticated && gridContainer) {
      const preventScroll = (e) => {
        logger.log('Blocked scroll event:', e.type); // Debug log
        e.preventDefault();
        e.stopPropagation();
      };

      // Block scroll and drag-related events
      gridContainer.addEventListener('wheel', preventScroll, { passive: false });
      gridContainer.addEventListener('scroll', preventScroll, { passive: false });
      gridContainer.addEventListener('touchmove', preventScroll, { passive: false });
      gridContainer.addEventListener('mousedown', (e) => {
        // Block mousedown on scrollbar or grid content
        if (e.target.closest('.MuiDataGrid-virtualScroller') || e.target.closest('.MuiDataGrid-scrollbarFiller')) {
          logger.log('Blocked mousedown event for drag scrolling');
          e.preventDefault();
          e.stopPropagation();
        }
      }, { passive: false });
      gridContainer.addEventListener('mousemove', (e) => {
        // Block mousemove during drag attempts
        if (e.target.closest('.MuiDataGrid-virtualScroller') || e.target.closest('.MuiDataGrid-scrollbarFiller')) {
          logger.log('Blocked mousemove event for drag scrolling');
          e.preventDefault();
          e.stopPropagation();
        }
      }, { passive: false });

      return () => {
        gridContainer.removeEventListener('wheel', preventScroll);
        gridContainer.removeEventListener('scroll', preventScroll);
        gridContainer.removeEventListener('touchmove', preventScroll);
        gridContainer.removeEventListener('mousedown', preventScroll);
        gridContainer.removeEventListener('mousemove', preventScroll);
      };
    }
  }, [isAuthenticated]);
  const filterRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);
  const leagueInitialized = useRef(false);
  const categoryInitialized = useRef(false);
  const betTypeInitialized = useRef(false);
  const bookInitialized = useRef(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Refs for immediate state access to prevent race conditions
  const trackingStateRef = useRef(new Set());
  const trackedBetsRef = useRef([]);

  // Update refs when state changes and sync immediately tracked
  useEffect(() => {
    trackedBetsRef.current = trackedBets;
    
    // Clean up immediately tracked set when actual tracked bets update
    const currentTrackedIds = new Set(trackedBets.map(bet => bet.id));
    setImmediatelyTracked(prev => {
      const filtered = new Set([...prev].filter(id => !currentTrackedIds.has(id)));
      return filtered.size !== prev.size ? filtered : prev;
    });
  }, [trackedBets]);

  useEffect(() => {
    trackingStateRef.current = trackingInProgress;
  }, [trackingInProgress]);

  // Monitor for duplicate bets
  useEffect(() => {
    const ids = trackedBets.map(bet => bet.id);
    const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
    
    if (duplicates.length > 0) {
      logger.error('DUPLICATE BETS DETECTED:', duplicates);
      logger.error('All tracked bet IDs:', ids);
      logger.error('Tracked bets:', trackedBets);
    }
  }, [trackedBets]);

  // Enhanced trackBet with safeguards
  const safeguardedTrackBet = useCallback((bet) => {
    if (!isAuthenticated) {
      logger.log('Cannot track bet - user not authenticated');
      return;
    }
    
    logger.log(`[TRACK] Attempting to track bet ${bet.id}`);
    logger.log(`[TRACK] Current tracked count: ${trackedBetsRef.current.length}`);
    logger.log(`[TRACK] Already tracked: ${trackedBetsRef.current.some(b => b.id === bet.id)}`);
    
    // Check for duplicates before tracking
    if (trackedBetsRef.current.some(existing => existing.id === bet.id)) {
      logger.error(`[TRACK] DUPLICATE PREVENTED: Bet ${bet.id} already tracked`);
      return;
    }
    
    // Add to immediately tracked for instant hiding
    setImmediatelyTracked(prev => new Set(prev).add(bet.id));
    
    // Call the original trackBet function
    if (trackBet) {
      trackBet(bet);
      logger.log(`[TRACK] SUCCESS: Bet ${bet.id} tracking initiated`);
    }
  }, [trackBet, isAuthenticated]);

  // Enhanced untrackBet with logging
  const safeguardedUntrackBet = useCallback((betId) => {
    if (!isAuthenticated) {
      logger.log('Cannot untrack bet - user not authenticated');
      return;
    }
    
    logger.log(`[UNTRACK] Attempting to untrack bet ${betId}`);
    logger.log(`[UNTRACK] Current tracked count: ${trackedBetsRef.current.length}`);
    
    // Check if bet exists before untracking
    if (!trackedBetsRef.current.some(bet => bet.id === betId)) {
      logger.warn(`[UNTRACK] Bet ${betId} not found in tracked bets`);
      return;
    }
    
    // Remove from immediately tracked
    setImmediatelyTracked(prev => {
      const next = new Set(prev);
      next.delete(betId);
      return next;
    });
    
    // Call the original untrackBet function
    if (untrackBet) {
      untrackBet(betId);
      logger.log(`[UNTRACK] SUCCESS: Bet ${betId} untracking initiated`);
    }
  }, [untrackBet, isAuthenticated]);

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem('bankroll', bankroll.toString());
  }, [bankroll]);

  useEffect(() => {
    localStorage.setItem('kellyFraction', kellyFraction.toString());
  }, [kellyFraction]);

  useEffect(() => {
    localStorage.setItem('hideTracked', JSON.stringify(hideTracked));
  }, [hideTracked]);

  useEffect(() => {
    localStorage.setItem('showHiddenBets', JSON.stringify(showHiddenBets));
  }, [showHiddenBets]);

  useEffect(() => {
    localStorage.setItem('snipeMinEV', JSON.stringify(snipeMinEV));
  }, [snipeMinEV]);

  useEffect(() => {
    localStorage.setItem('snipeMaxAge', JSON.stringify(snipeMaxAge));
  }, [snipeMaxAge]);

  useEffect(() => {
    localStorage.setItem('breakEvenWinRange', JSON.stringify(breakEvenWinRange));
  }, [breakEvenWinRange]);

  useEffect(() => {
    localStorage.setItem('leagueFilter', JSON.stringify(leagueFilter));
  }, [leagueFilter]);

  useEffect(() => {
    localStorage.setItem('categoryFilter', JSON.stringify(categoryFilter));
  }, [categoryFilter]);

  useEffect(() => {
    localStorage.setItem('betTypeFilter', JSON.stringify(betTypeFilter));
  }, [betTypeFilter]);

  useEffect(() => {
    localStorage.setItem('bookFilter', JSON.stringify(bookFilter));
  }, [bookFilter]);

  useEffect(() => {
    localStorage.setItem('dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    setMinEVDisplay(snipeMinEV ?? 'No Min');
  }, [snipeMinEV]);

  useEffect(() => {
    setMaxAgeDisplay(snipeMaxAge ?? 'No Max');
  }, [snipeMaxAge]);

  // Debug height calculations and measure filter section height
  useEffect(() => {
    const logHeights = () => {
      const filterHeight = filterRef.current?.getBoundingClientRect().height || 56;
      logger.log('Debug Heights:');
      logger.log('Connection Panel Height (static): 48px');
      logger.log('Filter Section Height (static): 56px');
      logger.log('Filter Section Height (measured):', filterHeight, 'px');
      logger.log('Total Non-Table Height (static): 104px');
      logger.log('Screen Height (window.innerHeight):', window.innerHeight, 'px');
      logger.log('Calculated Table Height: calc(100vh - 104px) =', window.innerHeight - 104, 'px');
    };

    logHeights();
    const debouncedLog = debounce(logHeights, 100);
    window.addEventListener('resize', debouncedLog);
    return () => {
      window.removeEventListener('resize', debouncedLog);
      debouncedLog.cancel();
    };
  }, []);

  // Update currentTime every second for EV Age recalculation
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate grid width on mount and resize
  useEffect(() => {
    const updateGridWidth = debounce(() => {
      if (gridContainerRef.current) {
        setGridWidth(gridContainerRef.current.offsetWidth);
      }
    }, 100);
    updateGridWidth();
    window.addEventListener('resize', updateGridWidth);
    return () => {
      window.removeEventListener('resize', updateGridWidth);
      updateGridWidth.cancel();
    };
  }, []);

  // Enhanced toggle track function with stronger safeguards
  const toggleTrack = useCallback((row, trackBetFunc, untrackBetFunc) => {
    if (!isAuthenticated) {
      logger.log('Cannot toggle track - user not authenticated');
      return;
    }
    
    const id = row.id;
    
    // Check both tracking state and pending operations using refs for immediate access
    if (trackingStateRef.current.has(id)) {
      logger.log(`[TOGGLE] Tracking already in progress for bet ${id}`);
      return;
    }

    // Update tracking state immediately using refs
    const newTrackingState = new Set(trackingStateRef.current);
    newTrackingState.add(id);
    trackingStateRef.current = newTrackingState;
    setTrackingInProgress(newTrackingState);

    // Use current state from ref to determine action
    const alreadyTracked = trackedBetsRef.current.some(bet => bet.id === id);
    
    if (alreadyTracked) {
      logger.log(`[TOGGLE] Untracking bet ${id}`);
      safeguardedUntrackBet(id);
    } else {
      logger.log(`[TOGGLE] Tracking bet ${id}`);
      safeguardedTrackBet(row);
      
      if (hideTracked) {
        setPendingRemoval(prev => new Set(prev).add(id));
        setTimeout(() => {
          setPendingRemoval(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, 200);
      }
    }

    // Clear tracking progress with increased timeout
    setTimeout(() => {
      const clearedState = new Set(trackingStateRef.current);
      clearedState.delete(id);
      trackingStateRef.current = clearedState;
      setTrackingInProgress(clearedState);
    }, 500);

  }, [safeguardedTrackBet, safeguardedUntrackBet, hideTracked, isAuthenticated]);

  // Enhanced debouncing with more aggressive settings
  const debouncedToggleTrack = useCallback(
    debounce(toggleTrack, 100, { 
      leading: true, 
      trailing: false,
      maxWait: 200 // Prevents burst clicking from queuing multiple calls
    }), 
    [toggleTrack]
  );

  const toggleHide = useCallback((row) => {
    if (!isAuthenticated) {
      logger.log('Cannot hide bet - user not authenticated');
      return;
    }
    
    const id = row.id;
    const isHidden = hiddenBets.includes(id);
    if (!isHidden && !showHiddenBets) {
      setPendingRemoval(prev => new Set(prev).add(id));
      setTimeout(() => {
        setPendingRemoval(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 200);
    }
  }, [hiddenBets, showHiddenBets, isAuthenticated]);

  // Process bets
  const processedBets = useMemo(() => {
    if (!Array.isArray(bets)) return [];
    
    return bets
      .filter(bet => !bet.pickem && bet.roi != null && bet.roi <= 0.2)
      .map((row, idx) => {
        const id = row.id ?? `${idx}-${Date.now()}`;
        let side = row.side || '';
        if (side === 'Over') side = 'o';
        else if (side === 'Under') side = 'u';
        
        return {
          ...row,
          id,
          sidepoints: `${side}${row.points || ''}`.trim()
        };
      });
  }, [bets]);

  // Filter options and filtered bets
  const { filterOptions, filteredBets } = useMemo(() => {
    if (!processedBets.length) {
      logger.log('Number of Rows:', 0);
      return { filterOptions: { leagues: [], categories: [], betTypes: [], books: [] }, filteredBets: [] };
    }
    const leagues = new Set();
    const categories = new Set();
    const betTypes = new Set();
    const books = new Set();
    const trackedSet = new Set(trackedBets.map(bet => bet.id));
    const immediatelyTrackedSet = new Set(immediatelyTracked);
    const hiddenSet = new Set(hiddenBets);
    const pendingRemovalSet = new Set(pendingRemoval);
    const leagueSet = new Set(leagueFilter);
    const categorySet = new Set(categoryFilter);
    const betTypeSet = new Set(betTypeFilter);
    const bookSet = new Set(bookFilter);
    const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term);

    const filtered = [];
    for (let idx = 0; idx < processedBets.length; idx++) {
      const bet = processedBets[idx];
      if (bet.leagueName) leagues.add(bet.leagueName);
      if (bet.betCategory) categories.add(bet.betCategory);
      if (bet.betTypeName) betTypes.add(bet.betTypeName);
      if (bet.marketSourceName) books.add(bet.marketSourceName);
      if (['bookmaker', 'pinnacle'].includes(bet.marketSourceName?.toLowerCase())) continue;
      if (leagueFilter.length > 0 && bet.leagueName && !leagueSet.has(bet.leagueName)) continue;
      if (categoryFilter.length > 0 && bet.betCategory && !categorySet.has(bet.betCategory)) continue;
      if (betTypeFilter.length > 0 && bet.betTypeName && !betTypeSet.has(bet.betTypeName)) continue;
      if (bookFilter.length > 0 && bet.marketSourceName && !bookSet.has(bet.marketSourceName)) continue;
      if (hideTracked && (trackedSet.has(bet.id) || immediatelyTrackedSet.has(bet.id)) && !pendingRemovalSet.has(bet.id)) continue;
      if (!showHiddenBets && hiddenSet.has(bet.id) && !pendingRemovalSet.has(bet.id)) continue;
      if (snipeMinEV !== null && (bet.roi == null || bet.roi <= snipeMinEV / 100)) continue;
      if (snipeMaxAge !== null && bet.lastROIChangeTime) {
        const lastChangeTime = new Date(bet.lastROIChangeTime).getTime();
        const ageMinutes = (Date.now() - lastChangeTime) / 1000 / 60;
        if (ageMinutes >= snipeMaxAge) continue;
      }
      if (dateRange !== null && bet.eventStart) {
        const eventStartTime = new Date(bet.eventStart).getTime();
        const maxTime = Date.now() + dateRange * 24 * 60 * 60 * 1000;
        if (eventStartTime > maxTime) continue;
      }
      if (bet.breakEvenWin !== null) {
        const breakEvenPercent = bet.breakEvenWin * 100;
        if (breakEvenPercent < breakEvenWinRange[0] || breakEvenPercent > breakEvenWinRange[1]) continue;
      }
      // Apply search filter
      if (searchTerms.length > 0) {
        const eventText = (bet.eventShortName || '').toLowerCase();
        const playerText = (bet.playerName || '').toLowerCase();
        const betTypeText = (bet.betTypeName || '').toLowerCase();
        const bookText = (bet.marketSourceName || '').toLowerCase();
        let allTermsMatch = true;
        for (const term of searchTerms) {
          if (
            !eventText.includes(term) &&
            !playerText.includes(term) &&
            !betTypeText.includes(term) &&
            !bookText.includes(term)
          ) {
            allTermsMatch = false;
            break;
          }
        }
        if (!allTermsMatch) continue;
      }
      filtered.push(bet);
    }
    const validRowIds = new Set(filtered.map(row => row.id));
    const updatedExpandedRowIds = new Set(
      [...expandedRowIds].filter(id => validRowIds.has(id))
    );

    if (updatedExpandedRowIds.size !== expandedRowIds.size) {
      setExpandedRowIds(updatedExpandedRowIds);
    }
    logger.log('Number of Rows:', filtered.length);
    return {
      filterOptions: {
        leagues: Array.from(leagues),
        categories: Array.from(categories),
        betTypes: Array.from(betTypes).sort(),
        books: Array.from(books).filter(book => !['bookmaker', 'pinnacle'].includes(book?.toLowerCase())),
      },
      filteredBets: filtered,
    };
  }, [processedBets, leagueFilter, categoryFilter, betTypeFilter, bookFilter, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, trackedBets, hiddenBets, pendingRemoval, immediatelyTracked, searchQuery, dateRange, breakEvenWinRange]);

  // Remove the limitation - show all rows but blur the ones after the 3rd
  const displayBets = filteredBets;

  // Best odds calculation
  const bestOddsMap = useMemo(() => {
    const map = new Map();
    const groupedBets = new Map();
    
    processedBets.forEach((bet) => {
      if (!bet.betMarketId) return;
      const group = groupedBets.get(bet.betMarketId) || [];
      group.push(bet);
      groupedBets.set(bet.betMarketId, group);
    });

    groupedBets.forEach((group, betMarketId) => {
      const parsedGroup = group.map((bet) => ({
        ...bet,
        parsedSide: bet.side || '',
        parsedLine: Number(bet.points) || 0,
      }));

      const isOver = parsedGroup[0]?.parsedSide === 'Over';
      const orderedGroup = parsedGroup.sort((a, b) => {
        const lineA = a.parsedLine;
        const lineB = b.parsedLine;
        const priceA = a.price ?? -Infinity;
        const priceB = b.price ?? -Infinity;

        if (lineA !== lineB) {
          return isOver ? lineA - lineB : lineB - lineA;
        }
        return priceB - priceA;
      });

      const bestBet = orderedGroup[0];
      if (bestBet) {
        map.set(betMarketId, {
          bestOdds: bestBet.price,
          bestLine: bestBet.parsedLine,
        });
      }
    });

    return map;
  }, [processedBets]);

  // Associated bets map
  const associatedBetsMap = useMemo(() => {
    const map = new Map();
    processedBets.forEach((bet) => {
      if (!bet.betMarketId) return;
      const group = map.get(bet.betMarketId) || [];
      group.push(bet);
      map.set(bet.betMarketId, group);
    });
    return map;
  }, [processedBets]);

  const getAssociatedBets = useCallback((row) => {
    try {
      if (!row || !row.betMarketId || !associatedBetsMap) {
        return [];
      }
      
      const bets = associatedBetsMap.get(row.betMarketId);
      if (!bets || !Array.isArray(bets)) {
        return [];
      }
      
      // Filter out any invalid bets
      return bets.filter(bet => bet && bet.id);
    } catch (error) {
      logger.error('Error in getAssociatedBets:', error, 'Row:', row);
      return [];
    }
  }, [associatedBetsMap]);

  // Initialize filters when options become available
  useEffect(() => {
    if (filterOptions.leagues.length > 0 && 
        filterOptions.categories.length > 0 && 
        filterOptions.betTypes.length > 0 && 
        filterOptions.books.length > 0 &&
        !leagueInitialized.current) {
      
      const savedLeague = JSON.parse(localStorage.getItem('leagueFilter') || '[]');
      const savedCategory = JSON.parse(localStorage.getItem('categoryFilter') || '[]');
      const savedBetType = JSON.parse(localStorage.getItem('betTypeFilter') || '[]');
      const savedBook = JSON.parse(localStorage.getItem('bookFilter') || '[]');
      
      setLeagueFilter(savedLeague.length > 0 ? savedLeague : filterOptions.leagues);
      setCategoryFilter(savedCategory.length > 0 ? savedCategory : filterOptions.categories);
      setBetTypeFilter(savedBetType.length > 0 ? savedBetType : filterOptions.betTypes);
      setBookFilter(savedBook.length > 0 ? savedBook : filterOptions.books);
      
      leagueInitialized.current = true;
      categoryInitialized.current = true;
      betTypeInitialized.current = true;
      bookInitialized.current = true;
    }
  }, [filterOptions.leagues, filterOptions.categories, filterOptions.betTypes, filterOptions.books]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [leagueFilter, categoryFilter, betTypeFilter, bookFilter, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, searchQuery, dateRange, breakEvenWinRange]);

  // Filter handlers
  const handleFilterChange = useCallback((e, options, setter, debouncedSetter) => {
    const val = e.target.value;
    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        if (debouncedSetter) debouncedSetter(newValue);
        return newValue;
      });
    } else {
      if (debouncedSetter) {
        debouncedSetter(val);
      } else {
        setter(val);
      }
    }
  }, []);

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
  };

  // Export to CSV
  const exportToCSV = useCallback(() => {
    if (!filteredBets.length || !isAuthenticated) return;

    const headers = [
      'ID', 'League', 'Event', 'Event Start', 'Participant', 'Bet Type',
      'Book', 'Line', 'Odds', 'Implied%', 'Model%', 'Growth%', 'EV', 'Kelly', 'EV Age'
    ];

    const rows = filteredBets.map(bet => {
      const eventStart = bet.eventStart
        ? new Date(bet.eventStart).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).replace(',', '')
        : '';
      const odds = bet.price == null ? '' : bet.price < 0 ? bet.price : `+${bet.price}`;
      const implied = bet.breakEvenWin == null ? '' : `${(bet.breakEvenWin * 100).toFixed(1)}%`;
      const model = bet.trueWin == null ? '' : `${(bet.trueWin * 100).toFixed(1)}%`;
      const growth = bet.growthRate == null ? '' : `${(bet.growthRate * 100).toFixed(2)}%`;
      const ev = bet.roi == null ? '' : `${(bet.roi * 100).toFixed(1)}%`;
      const kelly = bet.kelly == null ? '' : `$${(bet.kelly * bankroll * kellyFraction).toFixed(2)}`;
      const evAge = bet.lastROIChangeTime
        ? `${Math.floor((Date.now() - new Date(bet.lastROIChangeTime).getTime()) / 1000 / 60)} min`
        : '';

      return [
        bet.id, bet.leagueName || '', bet.eventShortName || '', eventStart,
        bet.playerName || '', bet.betTypeName || '', bet.marketSourceName || '',
        bet.sidepoints || '', odds, implied, model, growth, ev, kelly, evAge
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `bets_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredBets, bankroll, kellyFraction, isAuthenticated]);

  // Settings handlers
  const handleSettingsOpen = useCallback((event) => {
    setSettingsAnchorEl(event.currentTarget);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  const handleSliderChange = useCallback((event, newValue) => {
    const [min, max] = newValue;
    setBreakEvenWinRange([Math.min(min, max), Math.max(min, max)]);
  }, []);

  // Desktop column definitions
  const staticColumns = [
    {
      field: 'eventShortName',
      headerName: 'Event',
      minWidth: 175,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Event
        </Typography>
      ),
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.leagueName || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {/* League name */}
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {/* Away team */}
              {params.row.awayLogo && (
                <img
                  src={params.row.awayLogo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '0.75rem', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {/* Home team */}
              {params.row.homeLogo && (
                <img
                  src={params.row.homeLogo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.875rem', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      minWidth: 165,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Bet Info
        </Typography>
      ),
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                paddingLeft: '8px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {playerName}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                paddingLeft: '8px',
                color: '#BBBBBB',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {betTypeName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'logourl',
      headerName: 'Book',
      minWidth: 90,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Book
        </Typography>
      ),
      renderCell: p => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
            paddingLeft: '8px',
          }}
        >
          <img
            src={p.value}
            alt="Logo"
            style={{ width: 72, height: 72, objectFit: 'contain' }}
            loading="lazy"
          />
        </div>
      ),
    },
    {
      field: 'price',
      headerName: 'Line/Odds',
      minWidth: 120,
      flex: 1,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Tooltip title="Shows the betting line (e.g., Over/Under points) and odds. Highlighted if best odds for the market.">
            <InfoIcon sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0.5 }} />
          </Tooltip>
          <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
            Line/Odds
          </Typography>
        </Box>
      ),
      renderCell: params => {
        const value = params.value == null ? '' : params.value < 0 ? params.value : `+${params.value}`;
        const betMarketId = params.row.betMarketId;
        const isBestOdds = betMarketId && bestOddsMap.has(betMarketId) && 
          bestOddsMap.get(betMarketId).bestOdds === params.value && 
          bestOddsMap.get(betMarketId).bestLine === Number(params.row.points);
        const sidepoints = params.row.sidepoints || '';
        let prefix = sidepoints[0] || '';
        let numberStr = sidepoints.slice(1);
        let formattedNumber = '';
        let paddedPrefix = '      ';
      
        if (sidepoints && numberStr) {
          const numberParts = numberStr.split('.');
          const integerPart = numberParts[0] || '';
          const decimalPart = numberParts[1] || '0';
          if (integerPart) {
            formattedNumber = `${integerPart}.${decimalPart}`;
            const spaces = 6 - formattedNumber.length - prefix.length;
            paddedPrefix = ' '.repeat(spaces) + prefix;
          }
        }
      
        return (
          <Box
            sx={{
              width: 'calc(100% + 16px)', // Extend beyond cell padding
              height: 'calc(100% + 16px)', // Extend beyond cell padding
              margin: '-8px', // Negative margin to counteract cell padding
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
              pl: '16px', // Adjusted padding for text
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', whiteSpace: 'pre' }}>{paddedPrefix}{formattedNumber}</Typography>
              <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', ml: '4px' }}>{value}</Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  // Dynamic columns
  const columns = useMemo(() => [
    ...staticColumns,
    {
      field: 'roi',
      headerName: 'EV',
      minWidth: 70,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          EV
        </Typography>
      ),
      renderCell: p => <RoiCell value={p.value} />
    },
    {
      field: 'growthRate',
      headerName: 'Growth%',
      minWidth: 140,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Tooltip title="(1 + w)^winProb × (1 − f)^loseProb − 1, where f = fraction of your bankroll for the bet, and w is the fraction of your bankroll gained on a winning bet. Kelly Criterion optimal is calculated as the % of your bankroll to bet that maximizes this value. This metric combines EV and risk into one comprehensive rating.">
            <InfoIcon sx={{ fontSize: '16px', color: '#ffffff', mr: 0.5 }} />
          </Tooltip>
          <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
            Growth%
          </Typography>
        </Box>
      ),
      renderCell: p => <GrowthRateCell value={p.value} />
    },
    {
      field: 'lastROIChangeTime',
      headerName: 'EV Age',
      type: 'number',
      minWidth: 100,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          EV Age
        </Typography>
      ),
      valueGetter: params => {
        if (!params.value) return null;
        const lastChangeTime = new Date(params.value).getTime();
        return Math.max(0, (Date.now() - lastChangeTime) / 1000);
      },
      renderCell: params => {
        const lastROIChangeTime = params.row.lastROIChangeTime;
        if (!lastROIChangeTime) return null;
        const ageSeconds = Math.max(0, (Date.now() - new Date(lastROIChangeTime).getTime()) / 1000);
        let displayValue = `${Math.floor(ageSeconds / 60).toString().padStart(3, ' ')} min`;
        return (
          <RoiAgeCell
            ageSeconds={ageSeconds}
            displayValue={displayValue}
            snipeMaxAge={snipeMaxAge}
          />
        );
      }
    },
    {
      field: 'kelly',
      headerName: 'Kelly',
      minWidth: 70,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Kelly
        </Typography>
      ),
      renderCell: p => <KellyCell value={p.value} bankroll={bankroll} kellyFraction={kellyFraction} />
    },
    {
      field: 'trackBet',
      headerName: 'Track',
      sortable: false,
      minWidth: 65,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Track
        </Typography>
      ),
      renderCell: p => (
        <TrackBetCell
          row={p.row}
          trackedBets={trackedBets}
          toggleTrack={debouncedToggleTrack}
          trackBet={safeguardedTrackBet}
          untrackBet={safeguardedUntrackBet}
          trackingInProgress={trackingInProgress}
          immediatelyTracked={immediatelyTracked || new Set()}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      field: 'hideBet',
      headerName: 'Hide',
      sortable: false,
      minWidth: 55,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Hide
        </Typography>
      ),
      renderCell: p => (
        <HideBetCell
          row={p.row}
          hiddenBets={hiddenBets}
          setHiddenBets={setHiddenBets}
          toggleHide={toggleHide}
          showHiddenBets={showHiddenBets}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      field: 'deeplink',
      headerName: '',
      sortable: false,
      minWidth: 80,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
        </Typography>
      ),
      renderCell: p => <DeeplinkCell value={p.value} isAuthenticated={isAuthenticated} />
    },
  ], [bankroll, kellyFraction, trackedBets, hiddenBets, showHiddenBets, snipeMaxAge, bestOddsMap, safeguardedTrackBet, safeguardedUntrackBet, debouncedToggleTrack, isAuthenticated]);

  // Detail panel for desktop
  const DetailPanelItem = React.memo(({ index, style, data }) => {
    const bet = data.bets[index];
    return (
      <Box
        sx={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          p: '0 11px',
          border: 'none',
          borderRadius: '4px',
          width: '100%',
        }}
      >
        <Box sx={{ mr: 0.5, transform: 'translateY(3px)' }}>
          {typeof bet.logourl === 'string' && bet.logourl ? (
            <img
              src={bet.logourl}
              alt="Book Logo"
              style={{ width: 44, height: 44, objectFit: 'contain' }}
              loading="lazy"
            />
          ) : (
            <Typography sx={{ fontSize: '0.875rem', color: '#ffffff' }}>No Logo</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              mr: 0.5,
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {bet.sidepoints || ' '}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {bet.price == null ? '' : bet.price < 0 ? bet.price : `+${bet.price}`}
          </Typography>
        </Box>
      </Box>
    );
  });

  const getDetailPanelContent = useCallback(({ row }) => {
    // More robust error checking
    if (!row || !row.id || !row.betMarketId) {
      logger.warn('getDetailPanelContent: Invalid row', row);
      return null;
    }
  
    const associatedBets = getAssociatedBets(row);
    if (!associatedBets || !Array.isArray(associatedBets) || associatedBets.length <= 1) {
      return null;
    }
  
    try {
      const allBets = [row, ...associatedBets.filter(bet => bet && bet.id && bet.id !== row.id)];
      
      // Ensure we have valid bets to work with
      if (allBets.length <= 1) {
        return null;
      }
  
      const parsedBets = allBets.map(bet => ({
        ...bet,
        parsedSide: bet.side || '',
        parsedLine: Number(bet.points) || 0,
      }));

      const isOver = parsedBets[0]?.parsedSide === 'Over';
      const orderedBets = parsedBets.sort((a, b) => {
        const lineA = a.parsedLine;
        const lineB = b.parsedLine;
        const priceA = a.price ?? -Infinity;
        const priceB = b.price ?? -Infinity;
  
        if (lineA !== lineB) {
          return isOver ? lineA - lineB : lineB - lineA;
        }
        return priceB - priceA;
      });
  
      const displayedBets = orderedBets.slice(0, 15);
      const mainRowIndex = displayedBets.findIndex(bet => bet && bet.id === row.id);
  

      return (
        <Box
          sx={{
            p: '2px 0',
            backgroundColor: '#2a2a2a',
            borderRadius: '4px',
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            flexWrap: 'nowrap',
            height: 52,
            '&::-webkit-scrollbar': {
              height: '7px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#333333',
              border: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888888',
              border: 'none',
              '&:hover': {
                backgroundColor: '#aaaaaa',
              },
            },
          }}
        >
          {displayedBets.map((bet, index) => {
            // Additional safety check for each bet
            if (!bet || !bet.id) {
              return null;
            }
            
            return (
              <Box
                key={bet.id || index}
                sx={{
                  width: 150,
                  flexShrink: 0,
                  flexGrow: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRight: '1px solid #ffffff',
                  pr: '11px',
                  backgroundColor: index === mainRowIndex ? 'rgba(0, 150, 255, 0.2)' : '#2a2a2a',
                }}
              >
                <DetailPanelItem
                  index={index}
                  data={{ bets: displayedBets }}
                />
              </Box>
            );
          })}
        </Box>
      );
    } catch (error) {
      logger.error('Error in getDetailPanelContent:', error, 'Row:', row);
      return null;
    }
  }, [getAssociatedBets]);
  
  const getDetailPanelHeight = useCallback((params) => {
    // Immediate failsafe - ensure we ALWAYS return a number
    const FALLBACK_HEIGHT = 0;
    
    try {
      // More robust error checking
      if (!params || !params.row || !params.id) {
        logger.warn('[getDetailPanelHeight] Invalid params - returning 0', params);
        return FALLBACK_HEIGHT;
      }
    
      const row = params.row;
      
      // Check if row has required properties
      if (!row.betMarketId) {
        logger.log('[getDetailPanelHeight] No betMarketId - returning 0 for rowId:', params.id);
        return FALLBACK_HEIGHT;
      }
      
      // Check if associatedBetsMap exists and has the betMarketId
      if (!associatedBetsMap || !associatedBetsMap.has(row.betMarketId)) {
        logger.log('[getDetailPanelHeight] No associatedBetsMap or betMarketId not found - returning 0 for rowId:', params.id, 'betMarketId:', row.betMarketId);
        return FALLBACK_HEIGHT;
      }
      
      // Get the associated bets and verify they exist
      const associatedBets = associatedBetsMap.get(row.betMarketId);
      if (!associatedBets || !Array.isArray(associatedBets) || associatedBets.length <= 1) {
        logger.log('[getDetailPanelHeight] Invalid associatedBets - returning 0 for rowId:', params.id, 'associatedBets:', associatedBets);
        return FALLBACK_HEIGHT;
      }
    
      const height = 52;
      
      // Final safety check to ensure we return a valid number
      return typeof height === 'number' && !isNaN(height) && isFinite(height) ? height : FALLBACK_HEIGHT;
      
    } catch (error) {
      logger.error('[getDetailPanelHeight] Unexpected error:', error, 'params:', params);
      logger.error('[getDetailPanelHeight] Stack trace:', error.stack);
      return FALLBACK_HEIGHT;
    }
  }, [associatedBetsMap]);

  const handleDetailPanelExpanded = useCallback((newExpandedRowIds) => {
    try {
      // Handle both array and Set inputs with additional safety checks
      let expandedArray;
      
      if (Array.isArray(newExpandedRowIds)) {
        expandedArray = newExpandedRowIds;
      } else if (newExpandedRowIds && typeof newExpandedRowIds[Symbol.iterator] === 'function') {
        expandedArray = Array.from(newExpandedRowIds);
      } else {
        logger.warn('handleDetailPanelExpanded: Invalid input type', typeof newExpandedRowIds, newExpandedRowIds);
        expandedArray = [];
      }
      
      // Ensure we have valid filteredBets data
      if (!filteredBets || !Array.isArray(filteredBets)) {
        logger.warn('handleDetailPanelExpanded: Invalid filteredBets', filteredBets);
        setExpandedRowIds(new Set());
        return;
      }
      
      const validRowIds = new Set(filteredBets.map(row => row && row.id).filter(Boolean));
      const filteredExpandedRowIds = expandedArray.filter(id => id && validRowIds.has(id));
      
      setExpandedRowIds(new Set(filteredExpandedRowIds));
    } catch (error) {
      logger.error('Error in handleDetailPanelExpanded:', error);
      setExpandedRowIds(new Set());
    }
  }, [filteredBets]);

  const columnVisibilityModel = useMemo(() => {
    return {
      eventStart: screenWidth > 1400,
      trueWin: screenWidth > 1400,
      breakEvenWin: screenWidth > 1400,
      growthRate: screenWidth > 1400,
    };
  }, [screenWidth]);

  // Mobile page setup
  const mobilePageSize = 10;
  const sortedBets = [...displayBets].sort((a, b) => {
    const aGrowth = a.growthRate || 0;
    const bGrowth = b.growthRate || 0;
    return bGrowth - aGrowth;
  });
  
  const startIdx = currentPage * mobilePageSize;
  const endIdx = startIdx + mobilePageSize;
  const paginatedBets = sortedBets.slice(startIdx, endIdx);
  const totalPages = Math.ceil(displayBets.length / mobilePageSize);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ 
          width: '100%', 
          height: '100vh', 
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Mobile Header */}
          <Box sx={{ 
  position: 'sticky',
  top: 0,
  backgroundColor: '#121212',
  px: 1.5,
  pt: 1.5,
  pb: 1.5,
  display: { xs: 'block', md: 'none' },
  borderBottom: '1px solid #333',
  zIndex: 10,
  minHeight: 72
}}>
  <Stack 
    direction="row" 
    justifyContent="space-between" 
    alignItems="center" 
    spacing={1}
    sx={{ height: '100%' }}
  >
    <Stack 
      direction="row" 
      spacing={.5}
      sx={{ 
        flex: 1.5, 
        minWidth: 0
      }}
    >
      <FormControl 
        variant="outlined" 
        size="small" 
        sx={{ 
          flex: 1, 
          minWidth: 100,
          maxWidth: 140
        }}
      >
        <InputLabel 
          id="mobile-league-filter-label" 
          shrink 
          sx={{ 
            color: '#ffffff', 
            fontSize: '0.875rem'
          }}
        >
          League
        </InputLabel>
        <Select
          multiple
          labelId="mobile-league-filter-label"
          label="League"
          notched
          value={leagueFilter}
          onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter)}
          renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
          disabled={!isAuthenticated}
          sx={{ 
            fontSize: '0.875rem',
            '& .MuiSelect-select': {
              paddingRight: '32px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#ffffff'
            },
            '&.Mui-disabled': {
              '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
            },
          }}
          MenuProps={{
            PaperProps: { style: { maxHeight: 300 } },
            disableAutoFocusItem: true,
            autoFocus: false,
          }}
        >
          <MenuItem value="all">
            <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
            <ListItemText primary="Select All" />
          </MenuItem>
          {filterOptions.leagues.map(o => (
            <MenuItem key={o} value={o}>
              <Checkbox checked={leagueFilter.includes(o)} />
              <ListItemText primary={o} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl 
        variant="outlined" 
        size="small" 
        sx={{ 
          flex: 1, 
          minWidth: 100,
          maxWidth: 140
        }}
      >
        <InputLabel 
          id="mobile-book-filter-label" 
          shrink 
          sx={{ 
            color: '#ffffff', 
            fontSize: '0.875rem'
          }}
        >
          Book
        </InputLabel>
        <Select
          multiple
          labelId="mobile-book-filter-label"
          label="Book"
          notched
          value={bookFilter}
          onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter)}
          renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
          disabled={!isAuthenticated}
          sx={{ 
            fontSize: '0.875rem',
            '& .MuiSelect-select': {
              paddingRight: '32px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#ffffff'
            },
            '&.Mui-disabled': {
              '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
            },
          }}
          MenuProps={{
            PaperProps: { style: { maxHeight: 300 } },
            disableAutoFocusItem: true,
            autoFocus: false,
          }}
        >
          <MenuItem value="all">
            <Checkbox checked={bookFilter.length === filterOptions.books.length} />
            <ListItemText primary="Select All" />
          </MenuItem>
          {filterOptions.books.map(o => (
            <MenuItem key={o} value={o}>
              <Checkbox checked={bookFilter.includes(o)} />
              <ListItemText primary={o} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
    
    <IconButton 
      onClick={handleSettingsOpen} 
      disabled={!isAuthenticated}
      sx={{ 
        color: '#ffffff', 
        flexShrink: 0,
        '&.Mui-disabled': { 
          color: '#ffffff',
          opacity: 0.5,
        },
      }}
    >
      <SettingsIcon sx={{ fontSize: '1.2rem' }} />
    </IconButton>
  </Stack>
</Box>

{/* Mobile Content */}
<Box sx={{ 
  flex: 1,
  overflowY: 'auto',
  p: 1.5,
  paddingBottom: totalPages > 1 ? '80px' : '16px',
  display: { xs: 'block', md: 'none' },
  position: 'relative',
}}>
  {/* Mobile blur overlay for unauthenticated users */}
  {!isAuthenticated && paginatedBets.length > 0 && (
    <>
      {/* Gradient overlay starting after the first card */}
      <Box
        sx={{
          position: 'absolute',
          top: '220px', // Approximate height of one card (adjust if needed)
          left: 0,
          right: 0,
          height: '60px',
          background: 'linear-gradient(to bottom, transparent, rgba(18, 18, 18, 0.8))',
          zIndex: 999,
          pointerEvents: 'none',
        }}
      />
      {/* Blur overlay covering cards after the first */}
      <Box
        sx={{
          position: 'absolute',
          top: '280px', // Gradient height (60px) + first card height (150px)
          left: 0,
          right: 0,
          bottom: totalPages > 1 ? '64px' : 0,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(18, 18, 18, 0.3)',
          zIndex: 1000,
          pointerEvents: 'none',
        }}
      />
      {/* Login prompt within the blurred section */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1001,
          width: '90%',
          maxWidth: 350,
        }}
      >
        <Box
          sx={{
            backgroundColor: '#1e1e1e',
            border: '2px solid #ff1744',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#BBBBBB', mb: 3 }}>
          Please log or subscribe to access all SharpStack features
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#ff1744',
                color: '#ffffff',
                '&:hover': { backgroundColor: '#d50000' },
              }}
              onClick={() => window.location.href = 'https://www.4for4.com/user/login'}
            >
              Log In
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#ff1744',
                color: '#ffffff',
                '&:hover': { backgroundColor: '#d50000' },
              }}
              onClick={() => window.location.href = 'https://www.4for4.com/plans'}
            >
              Subscribe
            </Button>
          </Stack>
        </Box>
      </Box>
    </>
  )}
  {/* Render only the first card if not authenticated */}
  {paginatedBets.slice(0, isAuthenticated ? undefined : 1).map((bet, index) => {
    const isBestOdds = bet.betMarketId && bestOddsMap.has(bet.betMarketId) && 
      bestOddsMap.get(bet.betMarketId).bestOdds === bet.price && 
      bestOddsMap.get(bet.betMarketId).bestLine === Number(bet.points);
    
    return (
      <MobileBetCard
        key={bet.id}
        bet={bet}
        bankroll={bankroll}
        kellyFraction={kellyFraction}
        trackedBets={trackedBets}
        hiddenBets={hiddenBets}
        setHiddenBets={setHiddenBets}
        showHiddenBets={showHiddenBets}
        toggleTrack={debouncedToggleTrack}
        toggleHide={toggleHide}
        trackBet={safeguardedTrackBet}
        untrackBet={safeguardedUntrackBet}
        trackingInProgress={trackingInProgress}
        associatedBets={getAssociatedBets(bet)}
        isBestOdds={isBestOdds}
        immediatelyTracked={immediatelyTracked || new Set()}
        isAuthenticated={isAuthenticated}
      />
    );
  })}
</Box>
         {/* Mobile Pagination */}
         {totalPages > 1 && (
            <Box sx={{ 
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#1e1e1e',
              borderTop: '1px solid #333',
              boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.5)', // Add shadow for separation
              p: 2,
              zIndex: 1002, // Above the blur overlay (1000) and login prompt (1001)
              flexShrink: 0,
              display: { xs: 'block', md: 'none' },
              opacity: isAuthenticated ? 1 : 0.5,
              pointerEvents: isAuthenticated ? 'auto' : 'none',
            }}>
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                <Button
                  size="small"
                  onClick={() => isAuthenticated && setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0 || !isAuthenticated}
                  sx={{ 
                    color: '#ffffff',
                    '&.Mui-disabled': { 
                      color: '#666666' 
                    }
                  }}
                >
                  Previous
                </Button>
                <Typography variant="body2" sx={{ color: isAuthenticated ? '#ffffff' : '#666666' }}>
                  {currentPage + 1} / {totalPages}
                </Typography>
                <Button
                  size="small"
                  onClick={() => isAuthenticated && setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                  disabled={currentPage >= totalPages - 1 || !isAuthenticated}
                  sx={{ 
                    color: '#ffffff',
                    '&.Mui-disabled': { 
                      color: '#666666' 
                    }
                  }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}

          {/* Desktop View */}
          <Box sx={{ 
            width: '100%', 
            height: '100vh', 
            backgroundColor: 'background.default', 
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column', 
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Filter Section */}
            <Box className="filter-section" sx={{ flex: 'none', boxSizing: 'border-box' }} ref={filterRef}>
  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
    <Stack direction="row" spacing={2}>
      {[
        { label: 'League', value: leagueFilter, set: setLeagueFilter, opts: filterOptions.leagues, id: 'league' },
        { label: 'Category', value: categoryFilter, set: setCategoryFilter, opts: filterOptions.categories, id: 'category' },
        { label: 'Bet Type', value: betTypeFilter, set: setBetTypeFilter, opts: filterOptions.betTypes, id: 'bet-type' },
        { label: 'Book', value: bookFilter, set: setBookFilter, opts: filterOptions.books, id: 'book' },
      ].map(({ label, value, set, opts, id }) => (
        <FormControl key={id} variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel id={`${id}-filter-label`} shrink sx={{ color: '#ffffff', fontSize: '0.875rem' }}>{label}</InputLabel>
          <Select
            multiple
            labelId={`${id}-filter-label`}
            label={label}
            notched
            value={value}
            onChange={e => handleFilterChange(e, opts, set)}
            renderValue={sel => renderFilterValue(sel, opts, label)}
            disabled={!isAuthenticated}
            sx={{ 
              fontSize: '0.875rem',
              '& .MuiSelect-select': { color: '#ffffff' },
              '&.Mui-disabled': {
                '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
              },
            }}
          >
            <MenuItem value="all">
              <Checkbox checked={value.length === opts.length} />
              <ListItemText primary="Select All" sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
            </MenuItem>
            {opts.map(o => (
              <MenuItem key={o} value={o}>
                <Checkbox checked={value.includes(o)} />
                <ListItemText primary={o} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
<TextField
  value={searchQuery}
  onChange={e => setSearchQuery(e.target.value)}
  placeholder="e.g. jokic mgm points"
  size="small"
  disabled={!isAuthenticated}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon sx={{ color: '#BBBBBB' }} />
      </InputAdornment>
    ),
  }}
  sx={{
    width: 225,
    '& .MuiInputBase-input': { 
      color: '#ffffff' 
    },
    '& .MuiInputBase-input::placeholder': { 
      color: '#BBBBBB', 
      opacity: 1 
    },
    '& .MuiInputLabel-root': { 
      fontSize: '0.875rem' 
    },
    // Override all disabled opacity
    '&.MuiTextField-root.Mui-disabled': {
      '& *': {
        opacity: '1 !important',
        '-webkit-text-fill-color': 'unset !important'
      },
      '& .MuiInputBase-input::placeholder': { 
        color: '#BBBBBB !important', 
        opacity: '1 !important',
        '-webkit-text-fill-color': '#BBBBBB !important'
      },
      '& .MuiOutlinedInput-notchedOutline': { 
        borderColor: '#ffffff' 
      }
    }
  }}
/>
    </Stack>
    <Stack direction="row" spacing={1} alignItems="center">
      <IconButton onClick={handleSettingsOpen} disabled={!isAuthenticated} sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}>
        <SettingsIcon sx={{ fontSize: '1.2rem' }} />
      </IconButton>
      <IconButton
        onClick={exportToCSV}
        disabled={filteredBets.length === 0 || !isAuthenticated}
        sx={{
          color: '#ffffff',
          '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
          '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 },
        }}
      >
        <DownloadIcon />
      </IconButton>
    </Stack>
  </Stack>
</Box>

            {/* Table Section */}
            <Box className="table-section" ref={gridContainerRef} sx={{ position: 'relative', flex: 1 }}>
              <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
              <DataGridPro
  rows={displayBets}
  columns={columns}
  pageSize={pageSize}
  onPageSizeChange={setPageSize}
  rowsPerPageOptions={[20, 50, 100]}
  pagination
  paginationMode="client"
  page={currentPage}
  onPageChange={setCurrentPage}
  sortingMode="client"
  sortModel={sortModel}
  onSortModelChange={newModel => setSortModel(newModel)}
  disableSelectionOnClick
  disableColumnFilter
  disableColumnMenu
  loading={false}
  getRowId={row => row.id}
  getRowClassName={(params) => params.row.isDetail ? 'detail-row' : ''}
  getDetailPanelContent={getDetailPanelContent}
  getDetailPanelHeight={getDetailPanelHeight}
  detailPanelExpandedRowIds={expandedRowIds}
  onDetailPanelExpandedRowIdsChange={handleDetailPanelExpanded}
  hideFooterSelectedRowCount
  
  columnVisibilityModel={columnVisibilityModel}
  components={{ Toolbar: GridToolbar }}
  sx={{
    '& .MuiDataGrid-columnHeaders': { 
      backgroundColor: '#1e1e1e', 
      borderBottom: '2px solid #444',
      position: 'sticky',
      top: '56px',
      zIndex: 2,
    },
    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
    '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
    '& .MuiDataGrid-cell': {
      color: '#ffffff !important',
      userSelect: 'none',
      fontSize: '0.875rem',
      borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
    },
    '& .MuiDataGrid-cell:last-child': {
      borderRight: 'none', // Remove border from last column
    },
    '& .MuiDataGrid-cell:focus': { outline: 'none' },
    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
    '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
    '& .MuiDataGrid-columnSeparator': { 
      visibility: 'visible',
      color: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiDataGrid-iconButtonContainer': { 
      color: '#ffffff !important', 
      padding: '0px',
      backgroundColor: 'transparent !important',
      '&:hover': { backgroundColor: 'transparent !important' },
    },
    '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { 
      color: '#ffffff !important',
      fontSize: '0.875rem',
    },
    '& .MuiSelect-icon': { color: '#ffffff !important' },
    '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
    '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
    '& .MuiDataGrid-main': {
      padding: 0,
      boxSizing: 'border-box',
      overflow: 'hidden', // Prevent main container overflow
    },
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: '#1e1e1e',
      color: '#ffffff !important',
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
      boxSizing: 'border-box',
    },
    ...(!isAuthenticated && {
      '& .MuiDataGrid-virtualScroller': {
        overflowY: 'hidden !important',
        overflowX: 'hidden !important',
        pointerEvents: 'none',
        height: '156px',               // lock height to 3 rows × 52px
        '&::-webkit-scrollbar': {
          display: 'none',
          width: 0,
          height: 0,
        },
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        overflow: 'hidden !important',
        pointerEvents: 'none',
        height: '156px',
      },
      '& .MuiDataGrid-scrollbar': {
        display: 'none !important', // Explicitly hide scrollbar
      },
    }),
  }}
  rowBuffer={10}
  rowThreshold={5}
/>
              </Box>
{/* Desktop blur overlay for unauthenticated users */}
{!isAuthenticated && filteredBets.length > 3 && (
  <>
    {/* Blur effect applied directly after the 3rd row */}
    <Box
      sx={{
        position: 'absolute',
        top: '212px', // Header (56px) + 3 rows * 52px
        left: 0,
        right: 0,
        bottom: 0,
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        backgroundColor: 'rgba(18, 18, 18, 0.35)',
        zIndex: 11,
        pointerEvents: 'none',
      }}
    />
    {/* Login prompt overlay */}
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 12,
      }}
    >
      <Box
        sx={{
          backgroundColor: '#1e1e1e',
          border: '2px solid #ff1744',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        }}
      >
        <Typography variant="body1" sx={{ color: '#BBBBBB', mb: 3 }}>
          Please log or subscribe to access all SharpStack features
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#ff1744',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#d50000' },
            }}
            onClick={() => window.location.href = 'https://www.4for4.com/user/login'}
          >
            Log In
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#ff1744',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#d50000' },
            }}
            onClick={() => window.location.href = 'https://www.4for4.com/plans'}
          >
            Subscribe
          </Button>
        </Stack>
      </Box>
    </Box>
  </>
)}
            </Box>
          </Box>

          {/* Settings Popover */}
          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={{ p: '12px 16px', width: 320, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small" margin="dense">
                  <InputLabel id="date-range-label" sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Date Range</InputLabel>
                  <Select
                    labelId="date-range-label"
                    label="Date Range"
                    value={dateRange}
                    onChange={e => setDateRange(e.target.value === 'null' ? null : Number(e.target.value))}
                    sx={{ 
                      fontSize: '0.875rem', 
                      '& .MuiSelect-select': { color: 'white' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                    }}
                  >
                    <MenuItem value={1}>1 Day</MenuItem>
                    <MenuItem value={2}>2 Days</MenuItem>
                    <MenuItem value={7}>7 Days</MenuItem>
                    <MenuItem value={14}>14 Days</MenuItem>
                    <MenuItem value="null">Full Range</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  notched
                  label="Min EV (%)"
                  type="text"
                  value={minEVDisplay}
                  onChange={e => {
                    const val = e.target.value;
                    setMinEVDisplay(val);
                    if (val === '') {
                      setSnipeMinEV(null);
                    } else {
                      const num = parseFloat(val);
                      setSnipeMinEV(isNaN(num) ? null : num);
                    }
                  }}
                  onFocus={e => {
                    if (e.target.value === 'No Min') {
                      setMinEVDisplay('');
                    }
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if (val === '' || isNaN(parseFloat(val))) {
                      setSnipeMinEV(null);
                      setMinEVDisplay('No Min');
                    } else {
                      const num = parseFloat(val);
                      setSnipeMinEV(num);
                      setMinEVDisplay(num.toString());
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
                <TextField
                  notched
                  label="Max EV Age (min)"
                  type="text"
                  value={maxAgeDisplay}
                  onChange={e => {
                    const val = e.target.value;
                    setMaxAgeDisplay(val);
                    if (val === '') {
                      setSnipeMaxAge(null);
                    } else {
                      const num = parseInt(val);
                      setSnipeMaxAge(isNaN(num) || num < 0 ? null : num);
                    }
                  }}
                  onFocus={e => {
                    if (e.target.value === 'No Max') {
                      setMaxAgeDisplay('');
                    }
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if (val === '' || isNaN(parseInt(val)) || parseInt(val) < 0) {
                      setSnipeMaxAge(null);
                      setMaxAgeDisplay('No Max');
                    } else {
                      const num = parseInt(val);
                      setSnipeMaxAge(num);
                      setMaxAgeDisplay(num.toString());
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
                <TextField
                  notched
                  label="Bankroll"
                  type="text"
                  value={bankroll}
                  onChange={e => {
                    const val = e.target.value;
                    const num = parseFloat(val);
                    setBankroll(isNaN(num) || num < 0 ? '' : num);
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if (val === '' || isNaN(parseFloat(val)) || parseFloat(val) < 0) {
                      setBankroll(1000);
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography color="white">$</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
                <TextField
                  notched
                  label="Kelly Fraction"
                  type="text"
                  value={kellyFraction}
                  onChange={e => {
                    const val = e.target.value;
                    // Allow any input while typing, including decimals
                    setKellyFraction(val);
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    const num = parseFloat(val);
                    if (val === '' || isNaN(num) || num <= 0) {
                      setKellyFraction(1);
                    } else {
                      setKellyFraction(num);
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
              </Box>
              <Divider sx={{ borderColor: '#444', mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', mb: 1 }}>
                  Implied Odds Range (%)
                </Typography>
                <Slider
                  value={breakEvenWinRange}
                  onChange={handleSliderChange}
                  min={0}
                  max={100}
                  step={1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0, label: '0' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 75, label: '75' },
                    { value: 100, label: '100' },
                  ]}
                  sx={{
                    mt: 2,
                    '& .MuiSlider-valueLabel': {
                      fontSize: '0.75rem',
                    },
                    '& .MuiSlider-markLabel': {
                      fontSize: '0.75rem',
                    },
                  }}
                />
              </Box>
              <Divider sx={{ borderColor: '#444', mb: 2 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideTracked}
                    onChange={() => setHideTracked(h => !h)}
                    sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                  />
                }
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Hide Tracked Bets</Typography>}
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showHiddenBets}
                    onChange={() => setShowHiddenBets(h => !h)}
                    sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                  />
                }
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Show Hidden Bets</Typography>}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                <Button 
                  onClick={handleSettingsClose} 
                  sx={{ 
                    color: '#ffffff', 
                    fontSize: '0.875rem', 
                    minWidth: '80px',
                    '&:hover': { backgroundColor: '#333333' },
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSettingsClose} 
                  sx={{ 
                    color: '#ff1744', 
                    fontSize: '0.875rem', 
                    minWidth: '80px',
                    '&:hover': { backgroundColor: '#333333' },
                  }}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          </Popover>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

const EVTool = (props) => {
  return <BetsDataGrid {...props} />;
};

export default EVTool;